import { useEffect, useState } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, Search, Upload, KeyRound } from 'lucide-react';
import { ExportFormat, RbacApi, Staff, StaffApi } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import { Pagination } from '@/components/ui/Pagination';
import { ExportButtons } from '@/components/ui/ExportButtons';
import { ImportModal } from '@/components/shared/ImportModal';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Field, Input, Select, Textarea } from '@/components/ui/Input';
import { COUNTRY_CODES, DEFAULT_COUNTRY_CODE } from '@/lib/phone';
import { formatDate, formatMoney, paiseToRupees, rupeesToPaise } from '@/lib/format';

const ROLES = ['admin', 'manager', 'teacher', 'staff', 'cashier'] as const;
const BUILTIN_ROLES = new Set<string>([...ROLES, 'owner']);
const STATUSES = ['active', 'on_leave', 'resigned', 'terminated'] as const;

const schema = z.object({
  // User
  name: z.string().min(1, 'Required'),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  role: z.string().min(1, 'Required'), // built-in role name OR custom role key
  password: z.string().optional().or(z.literal('')),
  // Staff
  employeeId: z.string().min(1, 'Required'),
  designation: z.string().min(1, 'Required'),
  department: z.string().optional().or(z.literal('')),
  qualification: z.string().optional().or(z.literal('')),
  joiningDate: z.string().min(1, 'Required'),
  salaryRupees: z.coerce.number().min(0).optional(),
  bankAccount: z.string().optional().or(z.literal('')),
  bankIfsc: z.string().optional().or(z.literal('')),
  pan: z.string().optional().or(z.literal('')),
  aadhar: z.string().optional().or(z.literal('')),
  address: z.string().optional().or(z.literal('')),
  mobileCountryCode: z.string(),
  mobile: z.string().optional().or(z.literal('')),
  whatsappSameAsMobile: z.boolean(),
  whatsappCountryCode: z.string(),
  whatsapp: z.string().optional().or(z.literal('')),
  status: z.enum(STATUSES),
});
type FormValues = z.infer<typeof schema>;

/** Default reset-password value: the staff member's DOB reformatted DDMMYYYY with separators stripped (e.g. "1990-05-20" → "20051990") — per direct instruction, not a legacy pattern. Admin can overwrite before saving; blank (no dob on file) just leaves the password field empty. */
function dobNoSeparators(dob?: string | null): string {
  if (!dob) return '';
  const [year, month, day] = dob.slice(0, 10).split('-');
  if (!year || !month || !day) return dob.slice(0, 10).replace(/\D/g, '');
  return `${day}${month}${year}`;
}

const statusTone: Record<Staff['status'], 'green' | 'amber' | 'slate' | 'red'> = {
  active: 'green',
  on_leave: 'amber',
  resigned: 'slate',
  terminated: 'red',
};

const roleTone: Record<string, 'blue' | 'indigo' | 'purple' | 'slate'> = {
  admin: 'purple',
  manager: 'indigo',
  teacher: 'blue',
  staff: 'slate',
  cashier: 'indigo',
};

export function StaffPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [modal, setModal] = useState<{ open: boolean; staff?: Staff }>({
    open: false,
  });
  const [confirm, setConfirm] = useState<{ open: boolean; staff?: Staff }>({
    open: false,
  });
  const [resetPasswordFor, setResetPasswordFor] = useState<Staff | null>(null);
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  useEffect(() => {
    setPage(1);
  }, [search, role, limit]);

  const { data: pageData, isLoading } = useQuery({
    queryKey: ['staff', page, limit, search, role],
    queryFn: () =>
      StaffApi.list({ page, limit, search: search || undefined, role: role || undefined }),
    placeholderData: keepPreviousData,
  });
  const staff = pageData?.items ?? [];

  // Open the edit modal when arriving from the staff profile's "Edit" button.
  const editId = (location.state as { editId?: string } | null)?.editId;
  useEffect(() => {
    if (editId && staff.length) {
      const s = staff.find((x) => x.id === editId);
      if (s) {
        setModal({ open: true, staff: s });
        navigate(location.pathname, { replace: true, state: null });
      }
    }
  }, [editId, staff, navigate, location.pathname]);

  const upsert = useMutation({
    mutationFn: (v: { id?: string; payload: Record<string, unknown> }) =>
      v.id ? StaffApi.update(v.id, v.payload) : StaffApi.create(v.payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staff'] });
      qc.invalidateQueries({ queryKey: ['school-stats'] });
      setModal({ open: false });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => StaffApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staff'] });
      qc.invalidateQueries({ queryKey: ['school-stats'] });
      setConfirm({ open: false });
    },
  });

  // Teachers only (per direct instruction — "only teacher have login right").
  // Reuses the existing update() endpoint's password field, same as the
  // Edit form's "Reset login password" field — just a dedicated, faster
  // entry point defaulted to DOB instead of blank.
  const resetPassword = useMutation({
    mutationFn: (v: { id: string; password: string }) => StaffApi.update(v.id, { password: v.password }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staff'] });
      setResetPasswordFor(null);
    },
  });

  return (
    <>
      <PageHeader
        title="Staff"
        description="Teaching and non-teaching staff. Each entry creates a login user with the chosen role."
        actions={
          <div className="flex items-center gap-2">
            <ExportButtons
              onExport={(format: ExportFormat) =>
                StaffApi.export(format, { search: search || undefined, role: role || undefined })
              }
            />
            <button
              className="btn-secondary"
              onClick={() => setImportOpen(true)}
              title="Import staff from Excel"
            >
              <Upload className="mr-1.5 h-4 w-4" /> Import
            </button>
            <button
              className="btn-primary"
              onClick={() => setModal({ open: true })}
            >
              <Plus className="mr-1.5 h-4 w-4" /> Add staff
            </button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative w-64">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            placeholder="Search name, email, employee ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-md border border-slate-200 py-1.5 pl-9 pr-3 text-sm"
          />
        </div>
        <Select
          className="!w-40"
          value={role}
          onChange={(e) => setRole(e.target.value)}
        >
          <option value="">All roles</option>
          {ROLES.map((r) => (
            <option key={r} value={r} className="capitalize">
              {r}
            </option>
          ))}
        </Select>
      </div>

      <DataTable<Staff>
        rows={staff}
        getRowId={(r) => r.id}
        isLoading={isLoading}
        emptyMessage="No staff added yet."
        columns={[
          {
            key: 'no',
            header: 'No.',
            width: '1%',
            render: (_s, index) => <span className="text-slate-400">{(page - 1) * limit + index + 1}</span>,
          },
          {
            key: 'employeeId',
            header: 'Emp ID',
            render: (s) => (
              <code className="text-xs font-medium text-slate-700">
                {s.employeeId}
              </code>
            ),
          },
          {
            key: 'name',
            header: 'Name',
            render: (s) => (
              <div className="leading-tight">
                <button
                  className="font-medium text-slate-900 hover:text-brand-600 hover:underline"
                  onClick={() => navigate(`/staff/${s.id}`)}
                  title="View profile"
                >
                  {s.user?.name ?? '—'}
                </button>
                <div className="text-xs text-slate-500">{s.user?.email}</div>
              </div>
            ),
          },
          {
            key: 'role',
            header: 'Role',
            render: (s) => {
              const effective = s.user?.roleKey || s.user?.role;
              return (
                <Badge
                  tone={
                    s.user?.roleKey
                      ? 'green'
                      : roleTone[s.user?.role ?? 'staff'] ?? 'slate'
                  }
                >
                  {effective ?? '—'}
                </Badge>
              );
            },
          },
          {
            key: 'designation',
            header: 'Designation',
            render: (s) => (
              <div className="leading-tight">
                <div className="text-sm text-slate-700">{s.designation}</div>
                {s.department && (
                  <div className="text-xs text-slate-500">{s.department}</div>
                )}
              </div>
            ),
          },
          {
            key: 'joiningDate',
            header: 'Joined',
            render: (s) => formatDate(s.joiningDate),
          },
          {
            key: 'salary',
            header: 'Salary',
            render: (s) => (s.salary ? formatMoney(s.salary) : '—'),
          },
          {
            key: 'status',
            header: 'Status',
            render: (s) => <Badge tone={statusTone[s.status]}>{s.status}</Badge>,
          },
        ]}
        actions={(s) => (
          <>
            {(s.user?.role === 'teacher' || s.user?.roleKey === 'school_admin') && (
              <button
                className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-600"
                onClick={() => setResetPasswordFor(s)}
                title="Reset password"
              >
                <KeyRound className="h-4 w-4" />
              </button>
            )}
            <button
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-600"
              onClick={() => setModal({ open: true, staff: s })}
              title="Edit"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
              onClick={() => setConfirm({ open: true, staff: s })}
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </>
        )}
      />

      {pageData && (
        <Pagination
          page={pageData.page}
          totalPages={pageData.totalPages}
          total={pageData.total}
          limit={pageData.limit}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      )}

      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => {
          qc.invalidateQueries({ queryKey: ['staff'] });
          qc.invalidateQueries({ queryKey: ['school-stats'] });
        }}
        title="Import staff"
        description="Upload the .xlsx template. Each row creates a staff member and their login user."
        noun="staff member"
        onTemplate={() => StaffApi.importTemplate()}
        onPreview={(f) => StaffApi.importPreview(f)}
        onCommit={(f) => StaffApi.importCommit(f)}
      />

      <StaffFormModal
        open={modal.open}
        staff={modal.staff}
        onClose={() => setModal({ open: false })}
        saving={upsert.isPending}
        errorMsg={errMsg(upsert.error)}
        onSubmit={(v) =>
          upsert.mutate({
            id: modal.staff?.id,
            payload: {
              name: v.name,
              email: v.email || undefined,
              // Built-in role → set base enum; custom role → base 'staff' + roleKey.
              role: BUILTIN_ROLES.has(v.role) ? v.role : 'staff',
              roleKey: BUILTIN_ROLES.has(v.role) ? null : v.role,
              password: v.password || undefined,
              employeeId: v.employeeId,
              designation: v.designation,
              department: v.department || undefined,
              qualification: v.qualification || undefined,
              joiningDate: v.joiningDate,
              salary: v.salaryRupees ? rupeesToPaise(v.salaryRupees) : 0,
              bankAccount: v.bankAccount || undefined,
              bankIfsc: v.bankIfsc || undefined,
              pan: v.pan || undefined,
              aadhar: v.aadhar || undefined,
              address: v.address || undefined,
              mobileCountryCode: v.mobile ? v.mobileCountryCode : undefined,
              mobile: v.mobile || undefined,
              whatsappCountryCode: (
                v.whatsappSameAsMobile ? v.mobile : v.whatsapp
              )
                ? v.whatsappSameAsMobile
                  ? v.mobileCountryCode
                  : v.whatsappCountryCode
                : undefined,
              whatsapp:
                (v.whatsappSameAsMobile ? v.mobile : v.whatsapp) || undefined,
              status: v.status,
            },
          })
        }
      />

      <ConfirmDialog
        open={confirm.open}
        onClose={() => setConfirm({ open: false })}
        onConfirm={() => confirm.staff && remove.mutate(confirm.staff.id)}
        loading={remove.isPending}
        title="Delete staff?"
        message={`Soft-delete ${confirm.staff?.user?.name ?? 'this staff'}. Their login account is disabled.`}
        confirmText="Delete staff"
      />

      <ResetPasswordModal
        staff={resetPasswordFor}
        onClose={() => setResetPasswordFor(null)}
        onSubmit={(password) => resetPasswordFor && resetPassword.mutate({ id: resetPasswordFor.id, password })}
        saving={resetPassword.isPending}
        errorMsg={errMsg(resetPassword.error)}
      />
    </>
  );
}

function ResetPasswordModal({
  staff,
  onClose,
  onSubmit,
  saving,
  errorMsg,
}: {
  staff: Staff | null;
  onClose: () => void;
  onSubmit: (password: string) => void;
  saving: boolean;
  errorMsg?: string;
}) {
  const [password, setPassword] = useState('');
  const isSchoolAdmin = staff?.user?.roleKey === 'school_admin';

  useEffect(() => {
    if (!staff) return;
    setPassword(isSchoolAdmin ? staff.mobile ?? '' : dobNoSeparators(staff.user?.dob));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staff]);

  const defaultSource = isSchoolAdmin ? staff?.mobile : staff?.user?.dob;

  return (
    <Modal
      open={!!staff}
      onClose={() => {
        setPassword('');
        onClose();
      }}
      title={`Reset password — ${staff?.user?.name ?? ''}`}
      description={
        isSchoolAdmin
          ? 'Defaults to their mobile number. Edit it to set a different password, or leave as-is.'
          : 'Defaults to their date of birth (no separators). Edit it to set a different password, or leave as-is.'
      }
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => onSubmit(password)}
            disabled={saving || password.length < 8}
          >
            {saving ? 'Resetting…' : 'Reset password'}
          </button>
        </>
      }
    >
      <Field
        label="New password"
        hint={!defaultSource ? `No ${isSchoolAdmin ? 'mobile number' : 'date of birth'} on file — enter a password manually.` : 'At least 8 characters.'}
        error={password.length > 0 && password.length < 8 ? 'Minimum 8 characters' : undefined}
      >
        <Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password" autoFocus />
      </Field>
      {errorMsg && <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errorMsg}</div>}
    </Modal>
  );
}

function StaffFormModal({
  open,
  staff,
  onClose,
  onSubmit,
  saving,
  errorMsg,
}: {
  open: boolean;
  staff?: Staff;
  onClose: () => void;
  onSubmit: (v: FormValues) => void;
  saving: boolean;
  errorMsg?: string;
}) {
  const isEdit = !!staff;
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      name: staff?.user?.name ?? '',
      email: staff?.user?.email ?? '',
      role: staff?.user?.roleKey ?? staff?.user?.role ?? 'teacher',
      password: '',
      employeeId: staff?.employeeId ?? '',
      designation: staff?.designation ?? '',
      department: staff?.department ?? '',
      qualification: staff?.qualification ?? '',
      joiningDate: staff?.joiningDate?.slice(0, 10) ?? '',
      salaryRupees: staff ? paiseToRupees(staff.salary) : 0,
      bankAccount: staff?.bankAccount ?? '',
      bankIfsc: staff?.bankIfsc ?? '',
      pan: staff?.pan ?? '',
      aadhar: staff?.aadhar ?? '',
      address: staff?.address ?? '',
      mobileCountryCode: staff?.mobileCountryCode ?? DEFAULT_COUNTRY_CODE,
      mobile: staff?.mobile ?? '',
      whatsappSameAsMobile: !staff?.whatsapp
        ? true
        : staff.whatsapp === (staff.mobile ?? '') &&
          (staff.whatsappCountryCode ?? DEFAULT_COUNTRY_CODE) ===
            (staff.mobileCountryCode ?? DEFAULT_COUNTRY_CODE),
      whatsappCountryCode: staff?.whatsappCountryCode ?? DEFAULT_COUNTRY_CODE,
      whatsapp: staff?.whatsapp ?? '',
      status: (staff?.status as FormValues['status']) ?? 'active',
    },
  });

  const { data: allRoles = [] } = useQuery({
    queryKey: ['roles'],
    queryFn: RbacApi.roles,
  });
  const customRoles = allRoles.filter((r) => !r.isSystem);

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={staff ? `Edit ${staff.user?.name}` : 'New staff member'}
      description={
        isEdit
          ? 'Update staff details. Leave password blank to keep the existing one.'
          : 'Creates a user account with the chosen role and an employee record. Set a password to enable login.'
      }
      size="xl"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSubmit(onSubmit)}
            disabled={saving}
          >
            {saving ? 'Saving…' : staff ? 'Save changes' : 'Create staff'}
          </button>
        </>
      }
    >
      <form className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="sm:col-span-3">
          <h4 className="mb-2 font-medium text-slate-900">Login account</h4>
        </div>

        <Field label="Full name" required error={errors.name?.message}>
          <Input {...register('name')} />
        </Field>
        <Field label="Email" error={errors.email?.message}>
          <Input type="email" {...register('email')} />
        </Field>
        <Field label="Role" required error={errors.role?.message}>
          <Select {...register('role')}>
            <optgroup label="Built-in">
              {ROLES.map((r) => (
                <option key={r} value={r} className="capitalize">
                  {r}
                </option>
              ))}
            </optgroup>
            {customRoles.length > 0 && (
              <optgroup label="Custom">
                {customRoles.map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.name}
                  </option>
                ))}
              </optgroup>
            )}
          </Select>
        </Field>
        <Field
          label={isEdit ? 'Reset login password' : 'Login password'}
          hint={
            isEdit
              ? 'Leave blank to keep current. Set one to (re)enable their login.'
              : 'At least 8 characters. Required for this person to log in.'
          }
          error={errors.password?.message}
          className="sm:col-span-3"
        >
          <Input type="password" {...register('password')} placeholder="········" />
        </Field>

        <div className="sm:col-span-3 mt-2 border-t border-slate-200 pt-4">
          <h4 className="mb-2 font-medium text-slate-900">Employment</h4>
        </div>

        <Field label="Employee ID" required error={errors.employeeId?.message}>
          <Input {...register('employeeId')} placeholder="EMP001" />
        </Field>
        <Field label="Designation" required error={errors.designation?.message}>
          <Input {...register('designation')} placeholder="Senior Teacher" />
        </Field>
        <Field label="Department">
          <Input {...register('department')} placeholder="Science" />
        </Field>

        <Field label="Joining date" required error={errors.joiningDate?.message}>
          <Input type="date" {...register('joiningDate')} />
        </Field>
        <Field label="Monthly salary (₹)">
          <Input
            type="number"
            min={0}
            step="0.01"
            {...register('salaryRupees')}
          />
        </Field>
        <Field label="Status">
          <Select {...register('status')}>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Mobile">
          <div className="flex gap-2">
            <Select {...register('mobileCountryCode')} className="!w-24 shrink-0">
              {COUNTRY_CODES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code}
                </option>
              ))}
            </Select>
            <Input
              {...register('mobile')}
              placeholder="9876543210"
              className="flex-1"
            />
          </div>
        </Field>
        <Field label="WhatsApp">
          <label className="mb-1.5 flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-brand-600"
              {...register('whatsappSameAsMobile')}
            />
            Same as mobile
          </label>
          {!watch('whatsappSameAsMobile') && (
            <div className="flex gap-2">
              <Select
                {...register('whatsappCountryCode')}
                className="!w-24 shrink-0"
              >
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code}
                  </option>
                ))}
              </Select>
              <Input
                {...register('whatsapp')}
                placeholder="WhatsApp number"
                className="flex-1"
              />
            </div>
          )}
        </Field>

        <Field label="Qualification" className="sm:col-span-3">
          <Textarea rows={2} {...register('qualification')} />
        </Field>

        <div className="sm:col-span-3 mt-2 border-t border-slate-200 pt-4">
          <h4 className="mb-2 font-medium text-slate-900">KYC & Bank (optional)</h4>
        </div>

        <Field label="PAN">
          <Input {...register('pan')} placeholder="ABCDE1234F" />
        </Field>
        <Field label="Aadhaar">
          <Input {...register('aadhar')} placeholder="1234 5678 9012" />
        </Field>
        <Field label="Bank IFSC">
          <Input {...register('bankIfsc')} placeholder="HDFC0001234" />
        </Field>
        <Field label="Bank account">
          <Input {...register('bankAccount')} />
        </Field>
        <Field label="Address" className="sm:col-span-2">
          <Input {...register('address')} />
        </Field>

        {errorMsg && (
          <div className="sm:col-span-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {errorMsg}
          </div>
        )}
      </form>
    </Modal>
  );
}

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return (
    anyE?.response?.data?.error?.message ??
    anyE?.message ??
    'Something went wrong'
  );
}
